import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Alert, Card, Typography, Upload, message, Button, Space, Tag, Table } from 'antd';
import { InboxOutlined, ArrowLeftOutlined, CloudUploadOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { decryptData } from '../middleware/encryption';
import {
  API_ENDPOINT_BULK_UPLOAD,
  API_ENDPOINT_BULK_FILES,
  authHeaders,
  bulkStatusColor,
} from './bulkVerificationApi';
import './Bounce.css';

const { Content } = Layout;
const { Dragger } = Upload;
const { Text, Title } = Typography;

const API_ENDPOINT_FILE = 'https://api.app.exellius.com/api/bounce/upload';

// NOTE: adjust this to match how your app actually stores the logged-in user's role.
const getUserRole = () => {
  return sessionStorage.getItem('role');
};

const FileUpload = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileResponse, setFileResponse] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkFilesLoading, setBulkFilesLoading] = useState(false);

  const navigate = useNavigate();

  const isAdmin = getUserRole() === 'admin';
  const isBusy = fileLoading || bulkLoading;

  const fetchBulkFiles = useCallback(async () => {
  if (!isAdmin) return;

  setBulkFilesLoading(true);

  try {
    console.log(
      "Fetching bulk files from:",
      API_ENDPOINT_BULK_FILES
    );

    const res = await fetch(
      API_ENDPOINT_BULK_FILES,
      {
        headers: authHeaders(),
      }
    );

    const result = await res.json();

    console.log("Raw API response:", result);

    if (!res.ok) {
      throw new Error(
        result?.message ||
        "Failed to load bulk files"
      );
    }

    let data = decryptData(result?.data).data;

   

    setBulkFiles(
      Array.isArray(data)
        ? data
        : []
    );

  } catch (err) {

    console.error(
      "Bulk files error:",
      err
    );

    setBulkFiles([]);

    message.error(
      err.message ||
      "Failed to load bulk files"
    );

  } finally {

    setBulkFilesLoading(false);

  }
}, [isAdmin]);

  useEffect(() => {
    fetchBulkFiles();
  }, [fetchBulkFiles]);

  const uploadFile = async (file, endpoint, setLoadingFn, { isBulk = false, redirectTo } = {}) => {
    setFileError(null);
    setFileResponse(null);
    setLoadingFn(true);

    try {
      const token = sessionStorage.getItem('access_token');
      const myHeaders = new Headers();
      if (token) myHeaders.append('Authorization', `Bearer ${token}`);

      const formdata = new FormData();
      formdata.append('', file, file.name);

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      const res = await fetch(endpoint, requestOptions);
      const result = await res.json();

      if (!res.ok) {
        setFileError(result?.message || 'File upload failed');
        message.error(`${file.name} file upload failed.`);
      } else {
        let decryptedResult = result;
        if (result?.data) {
          decryptedResult = decryptData(result.data) || result.data;
        }
        console.log(`${isBulk ? 'Bulk upload' : 'File upload'} response:`, decryptedResult);
        message.success(`${file.name} file uploaded successfully.`);

        if (isBulk) {
          fetchBulkFiles(); // refresh the list so the new job shows up right away
        }

        if (decryptedResult?.job?.id) {
          setTimeout(() => {
            navigate(redirectTo ? redirectTo(decryptedResult.job.id) : `/verify/result/${decryptedResult.job.id}`);
          }, 1500);
        }
      }
    } catch (err) {
      setFileError(err?.message || 'File upload failed');
      message.error('File upload failed.');
    } finally {
      setLoadingFn(false);
    }
  };

  const handleFileUpload = (file) => uploadFile(file, API_ENDPOINT_FILE, setFileLoading);
  const handleBulkUpload = (file) =>
    uploadFile(file, API_ENDPOINT_BULK_UPLOAD, setBulkLoading, {
      isBulk: true,
      redirectTo: (id) => `/verify/bulk-result/${id}`,
    });

  const handleSelectFile = (file) => {
    setSelectedFile(file);
    setFileError(null);
    return false; // Prevent default upload
  };

  const handleUploadClick = () => {
    if (selectedFile) {
      handleFileUpload(selectedFile);
    } else {
      message.warning('Please select a file first');
    }
  };

  const handleBulkUploadClick = () => {
    if (selectedFile) {
      handleBulkUpload(selectedFile);
    } else {
      message.warning('Please select a file first');
    }
  };

  const handleResetFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setFileResponse(null);
  };

  const draggerProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    beforeUpload: handleSelectFile,
    onDrop: (e) => {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const bulkFileColumns = [
    { title: 'File Name', dataIndex: 'file_name', key: 'file_name', ellipsis: true },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={bulkStatusColor(status)}>{status}</Tag>,
    },
    { title: 'Total Emails', dataIndex: 'total_emails', key: 'total_emails' },
    { title: 'Processed', dataIndex: 'processed_emails', key: 'processed_emails' },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/verify/bulk-result/${record.id}`)}
        >
          View Result
        </Button>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout>
        <AppHeader />
        <Content className="bounce-page" style={{ padding: 20, overflowY: 'scroll', height: 'calc(100vh - 64px)' }}>
          <Button
            onClick={() => navigate(-1)}
            style={{ marginBottom: 20 }}
            icon={<ArrowLeftOutlined />}
          >
            Back
          </Button>
          <Card className="bounce-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <Title level={3} style={{ margin: 0 }}>Email Bounce Validation - File Upload</Title>
                <Text type="secondary">
                  Upload a CSV file containing emails for bulk bounce validation. Drag and drop or click to select.
                </Text>
              </div>
              {isAdmin && <Tag color="gold">Admin</Tag>}
            </div>

            <div style={{ marginTop: 24 }}>
              <Dragger {...draggerProps} disabled={isBusy}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: 500 }}>
                  Click or drag CSV file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for single or bulk upload. Only CSV files are accepted.
                </p>
              </Dragger>
            </div>

            {selectedFile && (
              <Card style={{ marginTop: 16, backgroundColor: '#f6f8fb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Selected File: </Text>
                    <Text>{selectedFile.name}</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Size: {(selectedFile.size / 1024).toFixed(2)} KB</Text>
                    </div>
                  </div>
                  <Space>
                    <Button onClick={handleResetFile} disabled={isBusy}>
                      Change File
                    </Button>
                    {isAdmin && (
                      <Button
                        icon={<CloudUploadOutlined />}
                        onClick={handleBulkUploadClick}
                        loading={bulkLoading}
                        disabled={fileLoading}
                      >
                        Bulk Upload
                      </Button>
                    )}
                   
                  </Space>
                </div>
              </Card>
            )}

            {fileError && <Alert message={fileError} type="error" showIcon style={{ marginTop: 16 }} />}
          </Card>

          {isAdmin && (
            <Card
              className="bounce-card"
              style={{ marginTop: 24 }}
              title="Bulk Verification Files"
              extra={
                <Button icon={<ReloadOutlined />} onClick={fetchBulkFiles} loading={bulkFilesLoading}>
                  Refresh
                </Button>
              }
            >
              <Table
                rowKey="id"
                loading={bulkFilesLoading}
                dataSource={bulkFiles}
                columns={bulkFileColumns}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default FileUpload;